import { API_ERROR_CODES } from '@solar/contracts';
import type {
  MandateCancellationStatus,
  MandateInternalStatus,
  MandateProviderStatus,
  MandateStatusSource,
  MandateSummaryView,
  PaginatedData,
  SupportedBankView,
} from '@solar/contracts';

import { apiClient } from '../../lib/api-client';
import { ApiError } from '../../lib/api-error';
import { toQuery } from '../../lib/api-query';

export interface MandateStatusHistoryEntry {
  id: string;
  mandateId: string;
  fromStatus: string | null;
  toStatus: string;
  providerStatus: MandateProviderStatus | null;
  source: MandateStatusSource;
  actorUserId: string | null;
  reason: string | null;
  createdAt: string;
}

export interface MandateCancellationRequestView {
  id: string;
  mandateId: string;
  requestReason: string;
  requestedAt: string;
  status: MandateCancellationStatus;
  assignedStaffUserId: string | null;
  submittedToProviderAt: string | null;
  providerSupportReference: string | null;
  confirmedAt: string | null;
  rejectedAt: string | null;
  rejectionReason: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MandateDetailView extends MandateSummaryView {
  contractReference?: string | null;
  bankAccountMasked?: string | null;
  statusHistory: MandateStatusHistoryEntry[];
  cancellationRequests: MandateCancellationRequestView[];
}

export interface MandateListFilters {
  page?: number;
  pageSize?: number;
  internalStatus?: MandateInternalStatus;
  customerId?: string;
  subscriptionId?: string;
}

export interface MandateCancellationListFilters {
  page?: number;
  pageSize?: number;
  status?: MandateCancellationStatus;
}

export const mandatesApi = {
  listSupportedBanks(): Promise<SupportedBankView[]> {
    return apiClient.get('/customer/supported-banks');
  },

  async getContractMandate(contractId: string): Promise<MandateSummaryView | null> {
    try {
      return await apiClient.get(`/customer/contracts/${contractId}/mandate`);
    } catch (error: unknown) {
      // The API uses a specific 404 for a valid contract with no mandate yet.
      // Preserve all other 404s so an invalid or inaccessible contract is not masked.
      if (
        error instanceof ApiError &&
        error.status === 404 &&
        error.code === API_ERROR_CODES.NOT_FOUND &&
        error.message === 'Mandate not found for this subscription.'
      ) {
        return null;
      }
      throw error;
    }
  },

  createContractMandate(
    contractId: string,
    input: { bankAccountId: string; idempotencyKey: string },
  ): Promise<MandateSummaryView> {
    return apiClient.post(`/customer/contracts/${contractId}/mandate`, input);
  },

  get(id: string): Promise<MandateSummaryView> {
    return apiClient.get(`/customer/mandates/${id}`);
  },

  checkStatus(id: string): Promise<MandateSummaryView> {
    return apiClient.post(`/customer/mandates/${id}/check-status`);
  },

  declareActivationPayment(id: string): Promise<MandateSummaryView> {
    return apiClient.post(`/customer/mandates/${id}/activation-payment-declared`);
  },

  requestCancellation(id: string, reason: string): Promise<MandateCancellationRequestView> {
    return apiClient.post(`/customer/mandates/${id}/cancellation-requests`, { reason });
  },

  listAdmin(filters: MandateListFilters = {}): Promise<PaginatedData<MandateSummaryView>> {
    return apiClient.get(`/admin/mandates${toQuery({ ...filters })}`);
  },

  getAdmin(id: string): Promise<MandateDetailView> {
    return apiClient.get(`/admin/mandates/${id}`);
  },

  checkStatusAdmin(id: string): Promise<MandateSummaryView> {
    return apiClient.post(`/admin/mandates/${id}/check-status`);
  },

  listCancellationRequests(
    filters: MandateCancellationListFilters = {},
  ): Promise<PaginatedData<MandateCancellationRequestView>> {
    return apiClient.get(`/admin/mandate-cancellation-requests${toQuery({ ...filters })}`);
  },

  getCancellationRequest(id: string): Promise<MandateCancellationRequestView> {
    return apiClient.get(`/admin/mandate-cancellation-requests/${id}`);
  },

  reviewCancellationRequest(
    id: string,
    input: { notes?: string },
  ): Promise<MandateCancellationRequestView> {
    return apiClient.post(`/admin/mandate-cancellation-requests/${id}/review`, input);
  },

  submitCancellationToProvider(
    id: string,
    input: { providerSupportReference?: string; notes?: string },
  ): Promise<MandateCancellationRequestView> {
    return apiClient.post(`/admin/mandate-cancellation-requests/${id}/submit-to-provider`, input);
  },

  confirmCancellation(
    id: string,
    input: { notes?: string },
  ): Promise<MandateCancellationRequestView> {
    return apiClient.post(`/admin/mandate-cancellation-requests/${id}/confirm`, input);
  },

  rejectCancellation(
    id: string,
    input: { rejectionReason: string; notes?: string },
  ): Promise<MandateCancellationRequestView> {
    return apiClient.post(`/admin/mandate-cancellation-requests/${id}/reject`, input);
  },

  listProviderBanks(): Promise<SupportedBankView[]> {
    return apiClient.get('/admin/payment-provider/banks');
  },

  refreshProviderBanks(): Promise<SupportedBankView[]> {
    return apiClient.post('/admin/payment-provider/banks/refresh');
  },
};
